import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  useAddDateOptions,
  useDateOptionsForEvent,
  useRemoveDateOptions,
} from "../api/date-options";
import {
  useCloseEventWithWinner,
  useGetEvent,
  useReopenEvent,
  useSetShowVoteLink,
} from "../api/events-api";
import { queryClient } from "../api/queryClient";
import { votersFor, votesFromVoters } from "../api/attendance";
import { useLogeMembers } from "../api/user-api";
import { useResetVotesForMember } from "../api/vote-api";
import AvatarCircle from "../components/Avatar";
import Toast from "../components/Toast";
import { AdminHeader, Card, Eyebrow, PrimaryButton } from "../components/dss/Primitives";
import { Icon } from "../components/dss/Icon";
import { Skeleton } from "../components/dss/Skeleton";
import { useSupabaseAuth } from "../supabase/SupabaseContext";
import { assertIsDefined } from "../utils/assertion";

const EditVotableEventPage = () => {
  const { eventId } = useParams();
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const { data: members } = useLogeMembers();
  const [selectedResetUser, setSelectedResetUser] = useState<number | "">("");
  const { member, hasRole } = useSupabaseAuth();
  const { data: event, isLoading: isLoadingEvent } = useGetEvent(eventId);
  const { data: dateOptions, isLoading } = useDateOptionsForEvent(eventId);
  const { mutateAsync: addDates } = useAddDateOptions();
  const { mutateAsync: removeDates } = useRemoveDateOptions();
  const { mutateAsync: resetVotesForMember } = useResetVotesForMember();
  const { mutateAsync: closeWithWinner } = useCloseEventWithWinner();
  const { mutateAsync: reopenEvent } = useReopenEvent();
  const { mutateAsync: setShowVoteLink } = useSetShowVoteLink();
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  // The date the host/admin has actually tapped to close the vote with —
  // never inferred from the tally. A leader can still change while the vote
  // is open, and picking one for them would turn "flest stemmer" into a
  // decision nobody actually made.
  const [selectedWinnerId, setSelectedWinnerId] = useState<number | null>(null);
  const navigate = useNavigate();

  const isAdmin = hasRole("ADMIN");
  const isHost = !!member && !!event && member.id === event.host;

  useEffect(() => {
    setSelectedDates(dateOptions?.map((d) => dayjs(d.date).toDate()) ?? []);
  }, [dateOptions]);

  // A date removed from the ballot can't stay chosen as its winner.
  useEffect(() => {
    setSelectedWinnerId((current) =>
      current !== null && !dateOptions?.some((d) => d.id === current) ? null : current,
    );
  }, [dateOptions]);

  const tallies = useMemo(
    () =>
      (dateOptions ?? []).map((d) => {
        // The host isn't asked, so a stray vote of theirs isn't counted.
        const votes = votesFromVoters(d.votes, event?.host ?? null);
        return {
          ...d,
          yes: votes.filter((v) => v.answer === 1).length,
          maybe: votes.filter((v) => v.answer === 0.5).length,
        };
      }),
    [dateOptions, event?.host],
  );
  // Purely informational — which date is currently ahead, shown as a hint
  // next to it. Never what "Luk og vælg vinder" actually closes with; that's
  // selectedWinnerId, and only a tap sets it.
  const leadingId = useMemo(() => {
    const leader = [...tallies].sort((a, b) => b.yes - a.yes || b.maybe - a.maybe)[0];
    return leader && (leader.yes > 0 || leader.maybe > 0) ? leader.id : undefined;
  }, [tallies]);
  const selectedWinner = tallies.find((d) => d.id === selectedWinnerId);

  const votedMemberIds = useMemo(
    () =>
      new Set(
        (dateOptions ?? []).flatMap((d) =>
          votesFromVoters(d.votes, event?.host ?? null).map((v) => v.user_id),
        ),
      ),
    [dateOptions, event?.host],
  );
  // The host is never "missing": they don't get a ballot.
  const voters = useMemo(() => votersFor(members ?? [], event?.host ?? null), [members, event?.host]);
  const missingMembers = useMemo(
    () => voters.filter((m) => !votedMemberIds.has(m.id)),
    [voters, votedMemberIds],
  );

  const onResetVotes = useCallback(async () => {
    if (!selectedResetUser || !eventId) {
      setToast({ message: "Vælg et medlem først", type: "error" });
      return;
    }
    try {
      await resetVotesForMember({ eventId: Number(eventId), memberId: selectedResetUser });
      setToast({ message: "Stemmer er blevet nulstillet", type: "success" });
    } catch (error) {
      setToast({ message: "Kunne ikke nulstille stemmer", type: "error" });
    }
    queryClient.invalidateQueries();
  }, [eventId, resetVotesForMember, selectedResetUser]);

  const onUpdateDates = useCallback(async () => {
    assertIsDefined(eventId);
    const removedDates = dateOptions?.filter(
      (dateOption) =>
        !selectedDates.some((selectedDate) => dayjs(selectedDate).isSame(dateOption.date, "day")),
    );
    const dateIds = removedDates?.map((d) => d.id) ?? [];

    try {
      if (dateIds.length > 0) {
        await removeDates({ dateOptionIds: dateIds });
      }
      const newDates = selectedDates.filter(
        (selectedDate) =>
          !dateOptions?.some((dateOption) => dayjs(selectedDate).isSame(dateOption.date, "day")),
      );
      if (newDates.length > 0) {
        await addDates({
          eventId: Number(eventId),
          selectedDates: newDates.map((d) => dayjs(d).format("YYYY-MM-DD")),
        });
      }
      queryClient.invalidateQueries(["dateOptions"]);
      setToast({ message: "Datoer er blevet opdateret", type: "success" });
    } catch (error) {
      setToast({ message: "Kunne ikke opdatere datoer", type: "error" });
    }
  }, [addDates, dateOptions, eventId, removeDates, selectedDates]);

  const onRemoveOne = (dateOptionId: number) => {
    setSelectedDates((prev) =>
      prev.filter((d) => !dateOptions?.find((o) => o.id === dateOptionId && dayjs(d).isSame(o.date, "day"))),
    );
  };

  const onCloseWithWinner = useCallback(async () => {
    assertIsDefined(eventId);
    if (!selectedWinner) return;
    try {
      await closeWithWinner({ eventId: Number(eventId), winningDate: selectedWinner.date });
      setToast({ message: "Afstemning lukket — vinder valgt", type: "success" });
      queryClient.invalidateQueries();
    } catch (error) {
      setToast({ message: "Kunne ikke lukke afstemningen", type: "error" });
    }
  }, [closeWithWinner, eventId, selectedWinner]);

  const onToggleShowVoteLink = useCallback(async () => {
    if (!event) return;
    try {
      await setShowVoteLink({ eventId: event.id, showVoteLink: !event.show_vote_link });
      queryClient.invalidateQueries();
    } catch (error) {
      setToast({ message: "Kunne ikke opdatere afstemningslinket", type: "error" });
    }
  }, [event, setShowVoteLink]);

  const onReopen = useCallback(async () => {
    assertIsDefined(eventId);
    try {
      await reopenEvent(Number(eventId));
      setToast({ message: "Afstemning genåbnet", type: "success" });
      queryClient.invalidateQueries();
    } catch (error) {
      setToast({ message: "Kunne ikke genåbne afstemningen", type: "error" });
    }
  }, [eventId, reopenEvent]);

  if (!event || isLoadingEvent || isLoading) {
    return (
      <div className="mx-auto max-w-md bg-paper pt-safe min-h-app flex flex-col">
        <div className="px-7 pt-2">
          <Skeleton className="w-12 h-3" />
          <Skeleton className="w-48 h-3 mt-4" />
          <Skeleton className="w-full h-10 mt-1.5" />
        </div>
        <div className="px-7 pt-5">
          <Skeleton className="w-40 h-4 mb-3" />
          <div className="flex flex-col gap-2">
            {[0, 1].map((i) => (
              <Skeleton key={i} className="w-full h-[62px] rounded-lg" />
            ))}
          </div>
          <Skeleton className="mt-3 w-full h-[300px] rounded-lg" />
          <Skeleton className="mt-3 w-full h-11 rounded-lg" />
        </div>
        <div className="px-7 pt-6">
          <Skeleton className="w-44 h-4 mb-2.5" />
          <Skeleton className="w-full h-[62px] rounded-lg" />
        </div>
      </div>
    );
  }

  // Managing a vote is for the event's own host, or an admin — nobody else,
  // however they got here (typing the URL, an old link to a samling they no
  // longer host).
  if (!isAdmin && !isHost) {
    return <Navigate to={`/event/${eventId}`} replace />;
  }

  return (
    <div className="mx-auto max-w-md bg-paper pt-safe min-h-app flex flex-col">
      <Toast
        message={toast?.message ?? ""}
        type={toast?.type ?? "success"}
        visible={!!toast}
        onClose={() => setToast(null)}
      />
      <AdminHeader
        back="Samling"
        onBack={() => navigate(`/event/${eventId}`)}
        title="Afstemning"
        sub={`${event.title.toUpperCase()} · ${votedMemberIds.size} AF ${voters.length} HAR STEMT`}
      />

      {/* The gate on whether anyone but the host/admin can even see the
          ballot. Buried in the old admin-only edit form before this page
          existed — a host setting up dates for the first time had no way to
          reach it at all, so their samling could end up with dates but no
          way for anyone to vote on them. */}
      <div className="px-7 pt-5">
        <Card className="p-3.5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display text-[13.5px] font-bold uppercase tracking-tight">
              Vis afstemningslink
            </div>
            <div className="font-mono text-[9px] tracking-[1.2px] text-ink3 uppercase mt-1">
              {event.show_vote_link ? "Medlemmer kan stemme" : "Skjult for medlemmer"}
            </div>
          </div>
          <button
            type="button"
            onClick={onToggleShowVoteLink}
            aria-pressed={!!event.show_vote_link}
            aria-label="Vis afstemningslink"
            className={`w-[42px] h-6 rounded-full flex items-center px-0.5 border flex-shrink-0 ${
              event.show_vote_link
                ? "bg-vote-yes border-vote-yes justify-end"
                : "bg-paper2 border-line justify-start"
            }`}
          >
            <span className="w-[18px] h-[18px] rounded-full bg-paper" />
          </button>
        </Card>
      </div>

      <div className="px-7 pt-5">
        <div className={`flex items-baseline justify-between ${tallies.length > 0 ? "mb-1" : "mb-3"}`}>
          <Eyebrow>[ Datoer på afstemningen ]</Eyebrow>
          <div className="font-mono text-[9.5px] tracking-wide text-ink3 uppercase">
            {tallies.length} valg
          </div>
        </div>
        {tallies.length > 0 && (
          <div className="font-mono text-[9.5px] tracking-wide text-ink3 uppercase mb-2.5">
            Tryk på en dato for at vælge den som vinder
          </div>
        )}
        <div className="flex flex-col gap-2">
          {tallies.map((d) => {
            const isSelected = selectedWinnerId === d.id;
            const isLeading = leadingId === d.id && !isSelected;
            return (
              <div
                key={d.id}
                data-testid={`date-option-${d.id}`}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedWinnerId((current) => (current === d.id ? null : d.id))}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault();
                  setSelectedWinnerId((current) => (current === d.id ? null : d.id));
                }}
                className={`flex items-center gap-3.5 py-3 px-3.5 rounded-lg border cursor-pointer ${
                  isSelected ? "bg-ink border-ink text-paper" : "bg-card border-line-soft"
                }`}
              >
                <div className="w-10">
                  <div
                    className={`font-mono text-[9px] tracking-wide font-medium uppercase ${
                      isSelected ? "opacity-70" : "text-ink3"
                    }`}
                  >
                    {dayjs(d.date).format("MMM")}
                  </div>
                  <div className="font-display font-extrabold text-2xl leading-none tracking-tight tabular-nums">
                    {dayjs(d.date).format("DD")}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`font-mono text-[10px] tracking-wide uppercase ${
                      isSelected ? "opacity-80" : "text-ink3"
                    }`}
                  >
                    {d.yes} JA · {d.maybe} MÅSKE
                  </div>
                  {isLeading && (
                    <div className="font-mono text-[9px] tracking-wide font-bold mt-1 flex items-center gap-1.5 text-ink3">
                      <Icon name="crown" size={9} />
                      FLEST STEMMER
                    </div>
                  )}
                </div>
                {isSelected && <Icon name="check" size={14} />}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveOne(d.id);
                  }}
                  className={isSelected ? "text-paper/70" : "text-clay"}
                >
                  <Icon name="trash-can" variant="regular" size={13} />
                </button>
              </div>
            );
          })}
        </div>

        <div className={`pt-6 flex flex-col gap-2`}>
          {event.status !== "confirmed" ? (
            <PrimaryButton
              tone="sage"
              onClick={onCloseWithWinner}
              disabled={!selectedWinner}
              meta={selectedWinner ? dayjs(selectedWinner.date).format("DD. MMM") : "Vælg en dato"}
            >
              <span className="inline-flex items-center gap-2">
                <Icon name="gavel" size={14} /> Luk og vælg vinder
              </span>
            </PrimaryButton>
          ) : (
            <button
              onClick={onReopen}
              className="w-full py-3.5 rounded-lg border border-line text-ink2 font-display text-[13px] font-bold uppercase tracking-wide flex items-center justify-center gap-2"
            >
              <Icon name="rotate-left" size={13} /> Genåbn afstemning
            </button>
          )}
        </div>

        <div className="mt-3 rounded-lg overflow-hidden border border-line-soft bg-card p-2 flex justify-center">
          <DayPicker
            mode="multiple"
            weekStartsOn={1}
            selected={selectedDates}
            onSelect={(dates) => setSelectedDates(dates ?? [])}
            className="dss-calendar"
            modifiersClassNames={{ selected: "!bg-ink !text-paper !font-bold" }}
          />
        </div>
        <button
          onClick={onUpdateDates}
          className="mt-3 w-full py-3 rounded-lg border border-ink font-display text-[13px] font-bold uppercase tracking-wide"
        >
          Gem datoer
        </button>
      </div>

      <div className="px-7 pt-6">
        <Eyebrow className="mb-2.5">[ Mangler at stemme · {missingMembers.length} ]</Eyebrow>
        <Card className="p-3.5">
          <div className="flex -space-x-1.5">
            {missingMembers.slice(0, 10).map((m) => (
              <AvatarCircle key={m.id} userId={m.id} size="xs" showName={false} />
            ))}
            {missingMembers.length === 0 && (
              <div className="font-mono text-[10.5px] text-ink3 uppercase">Alle har stemt</div>
            )}
          </div>
        </Card>
      </div>

      {/* A member's votes can only be reset by admin — nothing about
          managing your own samling's vote implies the power to wipe
          someone else's answer to it. */}
      {isAdmin && (
        <div className="px-7 pt-8 pb-8">
          <Eyebrow className="mb-2.5">[ Nulstil stemmer for medlem ]</Eyebrow>
          <div className="flex gap-2">
            <select
              className="flex-1 bg-card border border-line-soft rounded-lg px-3 py-2.5 font-mono text-base"
              value={selectedResetUser}
              onChange={(e) => setSelectedResetUser(e.target.value ? Number(e.target.value) : "")}
            >
              <option value="">Vælg</option>
              {members?.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <button
              onClick={onResetVotes}
              className="px-4 rounded-lg bg-clay text-paper font-display text-[12px] font-bold uppercase tracking-wide"
            >
              Nulstil
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditVotableEventPage;
